import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Loader2, TrendingUp, AlertTriangle, ArrowRight, CalendarCheck } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PERIOD_OPTIONS = [
  { key: '7d', label: '7 days' },
  { key: '30d', label: '30 days' },
  { key: '90d', label: '90 days' },
];

const BookingFunnelWidget = ({ token }) => {
  const [period, setPeriod] = useState('30d');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API_URL}/api/admin/booking-funnel?period=${period}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setData(r.data);
    } catch (_e) {
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [token, period]);

  useEffect(() => { fetchData(); }, [fetchData]);

  if (loading && !data) {
    return (
      <Card className="border-0 shadow-lg mb-8" data-testid="booking-funnel-loading">
        <CardContent className="p-8 flex items-center justify-center text-zinc-400">
          <Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading booking funnel…
        </CardContent>
      </Card>
    );
  }
  if (!data) return null;

  const topCount = data.stages?.[0]?.count || 0;
  const overall = data.overall_conversion || 0;

  // Tonal hint colors for conversion rate
  let convTone = 'text-zinc-700 bg-zinc-100';
  if (topCount > 0) {
    if (overall >= 30) convTone = 'text-emerald-700 bg-emerald-100';
    else if (overall >= 10) convTone = 'text-amber-700 bg-amber-100';
    else convTone = 'text-red-700 bg-red-100';
  }

  return (
    <Card className="border-0 shadow-lg mb-8" data-testid="booking-funnel-widget">
      <CardHeader className="border-b border-zinc-100 pb-3">
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <CardTitle className="text-lg font-heading flex items-center gap-2">
            <TrendingUp className="h-5 w-5 text-[#F56F05]" />
            Booking funnel
            <span className={`ml-2 text-xs font-bold px-2 py-0.5 rounded-full ${convTone}`} data-testid="funnel-overall-rate">
              {overall}% converted
            </span>
          </CardTitle>
          <div className="flex items-center gap-1 bg-zinc-100 rounded-full p-1">
            {PERIOD_OPTIONS.map(opt => (
              <button
                key={opt.key}
                type="button"
                onClick={() => setPeriod(opt.key)}
                className={`text-xs font-semibold px-3 py-1 rounded-full transition-all ${period === opt.key ? 'bg-white text-[#F56F05] shadow-sm' : 'text-zinc-500 hover:text-zinc-700'}`}
                data-testid={`funnel-period-${opt.key}`}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>
      </CardHeader>
      <CardContent className="p-4">
        {topCount === 0 ? (
          <div className="text-center py-8 text-zinc-500 text-sm" data-testid="funnel-empty-state">
            No quote requests in the last {period === '7d' ? '7 days' : period === '90d' ? '90 days' : '30 days'} yet.
          </div>
        ) : (
          <>
            <div className="space-y-2">
              {data.stages.map((stage, idx) => {
                const widthPct = topCount > 0 ? Math.max(8, (stage.count / topCount) * 100) : 0;
                const dropFromPrev = idx > 0 ? data.stages[idx - 1].count - stage.count : 0;
                return (
                  <div key={stage.key} data-testid={`funnel-stage-${stage.key}`}>
                    <div className="flex items-center justify-between text-xs mb-1">
                      <div className="flex items-center gap-2">
                        <span className="font-bold text-zinc-700">{stage.label}</span>
                        {idx > 0 && dropFromPrev > 0 && (
                          <span className="text-[10px] text-zinc-400">−{dropFromPrev} drop-off</span>
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="font-bold text-zinc-900">{stage.count}</span>
                        <span className="text-zinc-400">·</span>
                        <span className="text-zinc-500">{stage.rate_from_top}%</span>
                      </div>
                    </div>
                    <div className="h-7 rounded-lg bg-zinc-100 overflow-hidden">
                      <div
                        className={`h-full rounded-lg transition-all ${idx === 3 ? 'bg-emerald-500' : 'bg-[#F56F05]'}`}
                        style={{ width: `${widthPct}%` }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Footer signals */}
            <div className="mt-5 pt-4 border-t border-zinc-100 grid grid-cols-1 sm:grid-cols-2 gap-3">
              <FunnelFooterCard
                icon={CalendarCheck}
                iconBg="bg-emerald-100 text-emerald-700"
                label="Bookings created"
                primary={data.appointments_created}
                suffix=" appointments"
                hint=" · includes direct bookings"
              />
              <StaleQuotesCard count={data.stale_quotes} />
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
};

const FunnelFooterCard = ({ icon: Icon, iconBg, label, primary, suffix, hint }) => (
  <div className="flex items-center gap-3 px-3 py-2.5 rounded-xl bg-zinc-50">
    <div className={`w-9 h-9 rounded-lg ${iconBg} flex items-center justify-center shrink-0`}>
      <Icon className="h-4 w-4" />
    </div>
    <div className="min-w-0">
      <p className="text-xs text-zinc-500 uppercase tracking-wider font-semibold">{label}</p>
      <p className="text-sm text-zinc-700">
        <strong className="text-zinc-900 text-base">{primary}</strong>{suffix}
        <span className="text-zinc-400">{hint}</span>
      </p>
    </div>
  </div>
);

const StaleQuotesCard = ({ count }) => {
  const hasStale = count > 0;
  return (
    <Link
      to="/admin/quote-requests?status=new"
      className={`flex items-center gap-3 px-3 py-2.5 rounded-xl transition-colors ${hasStale ? 'bg-amber-50 hover:bg-amber-100' : 'bg-zinc-50'}`}
      data-testid="funnel-stale-link"
    >
      <div className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 ${hasStale ? 'bg-amber-100 text-amber-700' : 'bg-zinc-100 text-zinc-500'}`}>
        <AlertTriangle className="h-4 w-4" />
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-xs text-zinc-500 uppercase tracking-wider font-semibold">Stale quote requests</p>
        <p className="text-sm text-zinc-700">
          <strong className={`text-base ${hasStale ? 'text-amber-700' : 'text-zinc-900'}`}>{count}</strong>
          {hasStale ? ' still in "new" after 48 h — respond now' : ' all caught up'}
        </p>
      </div>
      {hasStale && <ArrowRight className="h-4 w-4 text-amber-700 shrink-0" />}
    </Link>
  );
};

export default BookingFunnelWidget;
